import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  Divider,
  Stack,
  Link,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Avatar,
  Badge,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert
} from '@mui/material';

import {
  Info as InfoIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Home as HomeIcon,
  Keyboard as KeyboardIcon,
  List as ListIcon,
  TextFields as FontIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  RadioButtonUnchecked as UncheckedIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon
} from '@mui/icons-material';

import { responseService } from '../../Services/response.service';
import { demandeDetailsService } from '../../Services/demande-details.service';
import { httpService } from '../../Services/http.service';
import { API_ENDPOINTS } from '../../config/api.config';
import { API } from '../../Services/axios';

// Icon helper (used to display different icons per content type)
const getResponseTypeIcon = (contentTypeName) => {
  switch (contentTypeName) {
    case 'PRODUCTS':
      return <HomeIcon />;
    case 'INPUT':
      return <KeyboardIcon />;
    case 'OTHER_CHOICES':
      return <ListIcon />;
    case 'TEXT':
      return <FontIcon />;
    case 'CHOICES':
      return <CheckCircleIcon />;
    default:
      return <InfoIcon />;
  }
};

// Couleurs des statuts
const getStatusColor = (status) => {
  switch (status) {
    case 'En cours':
      return 'info';
    case 'Etudié':
      return 'success';
    case 'Rejeté':
      return 'error';
    case 'completed':
      return 'success';
    case 'in_progress':
      return 'warning';
    case 'pending':
      return 'default';
    default:
      return 'default';
  }
};

const getResponseTypeLabel = (contentTypeName) => {
  switch (contentTypeName) {
    case 'PRODUCTS':
      return 'Produit';
    case 'INPUT':
      return 'Saisie';
    case 'OTHER_CHOICES':
      return 'Autre Choix';
    case 'TEXT':
      return 'Texte';
    case 'CHOICES':
      return 'Selection';
    default:
      return 'Autre';
  }
};

// Composant TabPanel pour les onglets
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const DemandeDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [demande, setDemande] = useState(null);
  const [responses, setResponses] = useState([]);
  const [project, setProject] = useState(null);
  const [steps, setSteps] = useState([]);
  const [sections, setSections] = useState([]);
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentsForDemand, setDocumentsForDemand] = useState([]);
  const [detailedContents, setDetailedContents] = useState({}); // Pour stocker les contents détaillés
  const [detailedSections, setDetailedSections] = useState({}); // Pour stocker les sections détaillées récupérées via API

  // Handler for tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Organize responses by steps -> sections -> responses (but we'll use sections primarily)
  const organizeResponsesBySteps = () => {
    // Build a map stepId -> { step, responses }
    const result = {};
    if (!Array.isArray(responses) || responses.length === 0) return result;

    // If responses include step info, group by step; otherwise put all under a default step
    responses.forEach(r => {
      const stepId = r.content?.step_id || r.step_id || 'no-step';
      if (!result[stepId]) {
        result[stepId] = { step: { id: stepId, title: stepId === 'no-step' ? 'Général' : `Étape ${stepId}` }, responses: [] };
      }
      result[stepId].responses.push(r);
    });

    return result;
  };

  // Render documents list
  const renderDocuments = () => {
    if (!documentsForDemand || documentsForDemand.length === 0) {
      return <Typography>Aucun document associé.</Typography>;
    }

    return (
      <Grid container spacing={2}>
        {documentsForDemand.map((doc, idx) => (
          <Grid item xs={12} sm={6} md={4} key={idx}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1">{doc.title || doc.name || `Document ${doc.id}`}</Typography>
                {doc.file && (
                  <Box sx={{ mt: 1 }}>
                    <Link href={doc.file} target="_blank" rel="noopener">Télécharger</Link>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  // Small date formatter fallback
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const d = new Date(dateString);
      return d.toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  // Récupérer les données de la demande
  useEffect(() => {
    const fetchDemande = async () => {
      try {
        setLoading(true);
        
        // Utiliser l'API réelle
        console.log('Récupération des détails de la demande depuis l\'API...');
          
          // Récupérer les détails de la demande (l'ID peut être un UUID)
          const demandeResult = await demandeDetailsService.getDemandeDetails(id);
          
          if (demandeResult.success) {
            const fetchedDemande = demandeResult.data;
            setDemande(fetchedDemande);

            // Récupérer les détails complets des documents associés
            if (fetchedDemande.documents && fetchedDemande.documents.length > 0) {
              try {
                const documentPromises = fetchedDemande.documents.map(docId =>
                  API.get(`/be/admin/documents/${docId}/`)
                );
                const documentResponses = await Promise.all(documentPromises);
                const fetchedDocuments = documentResponses.map(res => res.data);
                console.log('Détails des documents récupérés:', fetchedDocuments);
                setDocumentsForDemand(fetchedDocuments);
              } catch (docError) {
                console.error('Erreur lors de la récupération des détails des documents:', docError);
              }
            }

            // Récupérer les données du projet si disponible
            if (demandeResult.data.projet) {
              try {
                const projectData = await httpService.get(API_ENDPOINTS.PROJECTS.DETAIL(demandeResult.data.projet));
                console.log('Données du projet récupérées:', projectData);

                if (projectData && !projectData.name) {
                  projectData.name = projectData.title || projectData.nom || projectData.project_name || `Projet #${demandeResult.data.projet}`;
                  console.log('Nom du projet déterminé:', projectData.name);
                }

                setProject(projectData);

                // Récupérer les étapes du projet
                const stepsData = await httpService.get(API_ENDPOINTS.STEPS.LIST_BY_PROJECT(demandeResult.data.projet));
                console.log('Étapes reçues:', stepsData);
                let stepsArray = Array.isArray(stepsData.results) ? stepsData.results : Array.isArray(stepsData) ? stepsData : [];

                const projectId = demandeResult.data.projet;
                stepsArray = stepsArray.filter(step => {
                  const stepProjectId = step.projet || step.project_id;
                  const isCorrectProject = stepProjectId == projectId || stepProjectId === parseInt(projectId);
                  return isCorrectProject;
                });

                setSteps(stepsArray);

                // Récupérer les sections et contenus pour chaque étape
                if (stepsArray.length > 0) {
                  try {
                    // Use maps to deduplicate sections and contents by id
                    const allSectionsMap = {};
                    const allContentsMap = {};

                    for (const step of stepsArray) {
                      const stepIdKey = step && (step.id !== undefined && step.id !== null) ? String(step.id) : null;
                      const sectionsData = await httpService.get(API_ENDPOINTS.SECTIONS.LIST_BY_STEP(step.id));
                      const stepSections = Array.isArray(sectionsData.results) ? sectionsData.results : Array.isArray(sectionsData) ? sectionsData : [];

                      // Register sections (deduplicated by id)
                      stepSections.forEach(sec => {
                        if (sec) {
                          const sid = (sec.id !== undefined && sec.id !== null) ? String(sec.id) : null;
                          if (sid) allSectionsMap[sid] = sec;
                        }
                      });

                      for (const section of stepSections) {
                        const sectionIdForContents = (section && (section.id !== undefined && section.id !== null)) ? section.id : null;
                        const contentsData = await httpService.get(API_ENDPOINTS.CONTENTS.LIST_BY_SECTION(sectionIdForContents));
                        const sectionContents = Array.isArray(contentsData.results) ? contentsData.results : Array.isArray(contentsData) ? contentsData : [];

                        sectionContents.forEach(content => {
                          if (!content) return;
                          // Attach step id safely
                          try { content.step_id = stepIdKey ?? step.id; } catch (e) { /* ignore */ }
                          // Ensure content knows its section (some API responses omit this)
                          try { if (section && (section.id !== undefined && section.id !== null)) { content.section = content.section ?? section.id; content.section_id = content.section_id ?? section.id; } } catch (e) {}
                          const cid = (content.id !== undefined && content.id !== null) ? String(content.id) : null;
                          if (cid) allContentsMap[cid] = content;
                        });
                      }
                    }

                    const uniqueSections = Object.values(allSectionsMap);
                    const uniqueContents = Object.values(allContentsMap);

                    setSections(uniqueSections);
                    setContents(uniqueContents);
                    console.log('Sections récupérées:', uniqueSections);
                    console.log('Contenus récupérés:', uniqueContents);
                    // Après avoir récupéré les contenus, récupérer les réponses liées à la demande
                    try {
                      const fetchedResponses = await responseService.getResponses(id);
                      // Enrichir les réponses avec les objets content si possible
                      const contentsMap = {};
                      uniqueContents.forEach(c => { if (c && (c.id !== undefined && c.id !== null)) contentsMap[String(c.id)] = c; });

                      const enrichedResponses = Array.isArray(fetchedResponses) ? fetchedResponses.map(r => {
                        // Normalize the content reference from the response
                        const contentRef = r.content ?? r.content_id ?? r.contentId ?? null;
                        let contentObj = null;

                        if (contentRef && typeof contentRef === 'object') {
                          // If the API embedded a content object, normalize and ensure id is string
                          contentObj = contentRef;
                          try {
                            if (contentObj.id !== undefined && contentObj.id !== null) contentObj._normalizedId = String(contentObj.id);
                          } catch (e) {}
                        } else if (contentRef || contentRef === 0) {
                          contentObj = contentsMap[String(contentRef)];
                        }

                        if (contentObj) {
                          // Ensure r.content is a consistent object with id and section info
                          r.content = contentObj;
                        }
                        return r;
                      }) : [];

                      // Filtrer pour ne garder que les réponses liées à la demande courante
                      const filteredByDemande = enrichedResponses.filter(r => {
                        // Possibilités de champ qui réfèrent à la demande
                        const demandeRef = r.demande || r.demande_id || r.request_id || r.request || r.demandeId || r.requestId;
                        if (!demandeRef) return false;
                        // comparer en souplesse (string/number)
                        return String(demandeRef) === String(id);
                      });

                      setResponses(filteredByDemande);

                      // Récupérer les détails des sections utilisées par les contenus (uniquement pour les réponses affichées)
                      const sectionIds = new Set();
                      filteredByDemande.forEach(r => {
                        if (!r || !r.content) return;
                        // content.section can be object or primitive; try multiple fallbacks
                        const rawSection = r.content.section ?? r.content.section_id ?? r.content.sectionId ?? r.content.sectionId ?? null;
                        let sid = null;
                        if (rawSection || rawSection === 0) {
                          if (typeof rawSection === 'object') {
                            sid = rawSection.id ?? rawSection._id ?? rawSection?.pk ?? null;
                          } else {
                            sid = rawSection;
                          }
                        }

                        // If the content itself has section via nested property (some payloads use content.sectionId)
                        if (!sid && (r.content.section || r.content.section_id || r.content.sectionId)) {
                          const raw2 = r.content.section || r.content.section_id || r.content.sectionId;
                          sid = (typeof raw2 === 'object') ? (raw2.id ?? raw2._id ?? null) : raw2;
                        }

                        if (sid !== undefined && sid !== null) {
                          sectionIds.add(String(sid));
                        }
                      });

                      if (sectionIds.size > 0) {
                        const sectionPromises = Array.from(sectionIds).map(sid =>
                          API.get(`/be/admin/sections/${sid}/`).then(res => ({ id: sid, data: res.data })).catch(err => {
                            console.warn(`Impossible de récupérer la section ${sid}:`, err);
                            return null;
                          })
                        );

                        const sectionResults = await Promise.all(sectionPromises);
                        const sectionsMap = {};
                        sectionResults.forEach(item => {
                          if (item && (item.id !== undefined && item.id !== null)) sectionsMap[String(item.id)] = item.data;
                        });

                        setDetailedSections(sectionsMap);
                      }
                    } catch (respErr) {
                      console.warn('Erreur lors de la récupération des réponses:', respErr);
                    }
                  } catch (sectionsError) {
                    console.warn('Erreur lors de la récupération des sections/contenus:', sectionsError);
                  }
                }
              } catch (projectErr) {
                console.warn('Erreur lors de la récupération des données du projet:', projectErr);
              }
            }
          }
        } catch (err) {
          console.error('Erreur lors de la récupération de la demande:', err);
          setError('Erreur lors de la récupération des détails de la demande.');
        } finally {
          setLoading(false);
        }
      };

      fetchDemande();
    }, [id]);

    // Background fetch: when responses or contents change, fetch any content IDs referenced by responses that we don't yet have
    useEffect(() => {
      const fetchMissingContents = async () => {
        try {
          if (!Array.isArray(responses) || responses.length === 0) return;

          // Build current lookup from contents + detailedContents
          const currentContents = {};
          if (Array.isArray(contents)) contents.forEach(c => { if (c && c.id !== undefined && c.id !== null) currentContents[String(c.id)] = c; });
          if (detailedContents && typeof detailedContents === 'object') Object.keys(detailedContents).forEach(k => { if (detailedContents[k]) currentContents[String(k)] = detailedContents[k]; });

          const missing = new Set();
          responses.forEach(r => {
            const contentRef = r.content ?? r.content_id ?? r.contentId ?? null;
            if (contentRef && typeof contentRef !== 'object') {
              const key = String(contentRef);
              if (!currentContents[key]) missing.add(key);
            }
          });

          if (missing.size === 0) return;

          const promises = Array.from(missing).map(cid =>
            API.get(`/be/admin/contents/${cid}/`).then(res => ({ id: cid, data: res.data })).catch(err => {
              console.warn(`Impossible de récupérer le content ${cid}:`, err);
              return null;
            })
          );

          const results = await Promise.all(promises);
          const newDetailed = { ...(detailedContents || {}) };
          results.forEach(it => { if (it && it.id) newDetailed[String(it.id)] = it.data; });
          setDetailedContents(newDetailed);
        } catch (e) {
          console.warn('Erreur lors du fetch des contents manquants:', e);
        }
      };

      fetchMissingContents();
    }, [responses, contents]);

  const renderReponses = () => {
    if (!Array.isArray(responses) || responses.length === 0) {
      return (
        <Alert severity="info">Aucune réponse disponible pour cette demande.</Alert>
      );
    }

    const getResponseDisplayName = (response) => {
      if (response.content && response.content.name) return response.content.name;
      if (response.content && response.content.title) return response.content.title;
      if (response.content && response.content.description) return response.content.description;
      if (response.content && response.content.label) return response.content.label;
      return response.input_field_name || 'Réponse';
    };

    const getResponseDisplaySuffix = (response) => {
      if (!response.content) return '';
      if (response.content && response.content.content_type === 'INPUT') {
        // console.log('Récupération du suffixe de la réponse:', response.content.inputs[0].suffix);
        return response.content.inputs[0].suffix;
      };
      // if (response.content && response.content.content_type === 'OTHER_CHOICES') {
      //   // console.log('Récupération du suffixe de la réponse:', response.content.other_choices[0].suffix);
      //   return response.content.other_choices.content ;
      // };
      return '';
    };

    const getResponseDisplayValue = (response) => {
      if (!response.content) return response.value;
      switch (response.content.content_type) {
        case 'PRODUCTS': {
          const product = response.content.products?.find(p => p.id === response.value);
          return product ? product.name : `Produit ID: ${response.value}`;
        }
        case 'CHOICES': {
          const choice = response.content.choices?.find(c => c.id === response.value);
          return choice ? choice.name : `Choix ID: ${response.value}`;
        }
        case 'INPUT': {
          // const input = response.content.inputs?.find(i => i.id === response.value);
          // return input ? input.value : `Input ID: ${response.value}`;
        }
        case 'OTHER_CHOICES': {
          // const other_choice = response.content.other_choices?.find(c => c.id === response.value);
          // return other_choice ? other_choice.name : `Autre Choix ID: ${response.value}`;
        }
        default:
          return response.value;
      }
    };

    // Helper to normalize a raw section identifier to a string key
    const normalizeSectionId = (raw) => {
      if (raw === undefined || raw === null) return 'no-section';
      if (typeof raw === 'object') {
        const id = raw.id ?? raw._id ?? raw.section_id ?? raw.sectionId;
        return (id === undefined || id === null) ? 'no-section' : String(id);
      }
      return String(raw);
    };

    // Prepare a lookup for contents (by id) so we can resolve content ids to objects
    const contentsMapForLookup = {};
    if (Array.isArray(contents)) {
      contents.forEach(c => { if (c && (c.id !== undefined && c.id !== null)) contentsMapForLookup[String(c.id)] = c; });
    }
    // Merge detailedContents (object map) if present
    if (detailedContents && typeof detailedContents === 'object') {
      Object.keys(detailedContents).forEach(k => { if (detailedContents[k]) contentsMapForLookup[String(k)] = detailedContents[k]; });
    }

    // If some responses reference content IDs we don't yet have, show a small loading indicator
    const missingContentRefs = new Set();
    responses.forEach(r => {
      const cref = r.content ?? r.content_id ?? r.contentId ?? null;
      if (cref && typeof cref !== 'object') {
        const key = String(cref);
        if (!contentsMapForLookup[key]) missingContentRefs.add(key);
      }
    });

    if (missingContentRefs.size > 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>Chargement des contenus liés aux réponses...</Typography>
        </Box>
      );
    }

    // Group responses by normalized section id, resolving content IDs when needed
    const sectionGroups = {};
    responses.forEach(r => {
      // Ensure we have a content object on the response if possible
      let contentObj = null;
      const contentRef = r.content ?? r.content_id ?? r.contentId ?? null;
      if (contentRef && typeof contentRef === 'object') {
        contentObj = contentRef;
      } else if (contentRef || contentRef === 0) {
        contentObj = contentsMapForLookup[String(contentRef)] || null;
      }

      // If we resolved a content object, attach it to the response for consistent rendering
      if (contentObj) r.content = contentObj;

      // Extract the raw section from the content object when possible
      const rawSid = contentObj && (contentObj.section || contentObj.section_id || contentObj.sectionId) ||
                     (r.content && typeof r.content === 'object' && (r.content.section || r.content.section_id || r.content.sectionId)) ||
                     null;

      const sid = normalizeSectionId(rawSid);
      if (!sectionGroups[sid]) sectionGroups[sid] = { sectionId: sid, responses: [] };
      sectionGroups[sid].responses.push(r);
    });

    // Deduplicate OTHER_CHOICES responses per section: keep first occurrence per (contentId, value)
    try {
      Object.keys(sectionGroups).forEach(sid => {
        const grp = sectionGroups[sid];
        const seen = new Set();
        const filtered = [];
        grp.responses.forEach(resp => {
          try {
            const ctype = resp.content?.content_type;
            if (ctype === 'OTHER_CHOICES') {
              const contentId = resp.content?.id ?? resp.content?._normalizedId ?? 'unknown';
              const val = resp.value ?? resp.selected ?? JSON.stringify(resp.response_value ?? {});
              const key = `${String(contentId)}::${String(val)}`;
              if (!seen.has(key)) {
                seen.add(key);
                filtered.push(resp);
              } else {
                // duplicate - skip
              }
            } else {
              filtered.push(resp);
            }
          } catch (e) {
            filtered.push(resp);
          }
        });
        grp.responses = filtered;
      });
    } catch (e) {
      // if dedupe fails, leave original groups
      console.warn('Erreur lors du dedupe OTHER_CHOICES:', e);
    }

    console.log("sectionGroups: ", sectionGroups);

    // Diagnostics: detailed info for responses in 'no-section'
    try {
      const noSectionResponsesDetailed = [];
      const unresolvedContentRefs = new Set();
      const lookup = contentsMapForLookup;
      Object.entries(sectionGroups).forEach(([sid, grp]) => {
        if (sid === 'no-section') {
          grp.responses.forEach(r => {
            const rawRef = r.content ?? r.content_id ?? r.contentId ?? null;
            const contentObj = (rawRef && typeof rawRef === 'object') ? rawRef : (rawRef ? lookup[String(rawRef)] : null);
            const contentSection = contentObj ? (contentObj.section ?? contentObj.section_id ?? contentObj.sectionId ?? null) : null;
            if (rawRef && typeof rawRef !== 'object' && (!contentObj)) unresolvedContentRefs.add(String(rawRef));
            noSectionResponsesDetailed.push({ responseId: r.id ?? r.response_id ?? null, contentRef: rawRef, contentObj, contentSection, response: r });
          });
        }
      });
      if (noSectionResponsesDetailed.length > 0) {
        console.debug('Diagnostics: details for responses currently grouped as no-section:', noSectionResponsesDetailed.map(x => ({ responseId: x.responseId, contentRef: x.contentRef, contentSection: x.contentSection, contentObjKeys: x.contentObj ? Object.keys(x.contentObj) : null })));
      }
      if (unresolvedContentRefs.size > 0) console.debug('Diagnostics: unresolved content refs (no content object found):', Array.from(unresolvedContentRefs));
    } catch (e) {
      /* ignore diagnostics errors */
    }

    // Build a list of sections following the original `sections` array order.
    // We iterate over `sections` and collect groups in that order, then append any remaining groups.
    const sectionList = [];
    const added = new Set();

    if (Array.isArray(sections) && sections.length > 0) {
      for (const s of sections) {
        const sid = String(s.id);
        if (sectionGroups[sid]) {
          const meta = detailedSections[sid] || s || {};
          sectionList.push({ sid, group: sectionGroups[sid], meta });
          added.add(sid);
        }
      }
    }

    // Ensure 'no-section' appears if present and wasn't in sections
    if (sectionGroups['no-section'] && !added.has('no-section')) {
      const meta = detailedSections['no-section'] || { title: 'Général' };
      sectionList.push({ sid: 'no-section', group: sectionGroups['no-section'], meta });
      added.add('no-section');
    }

    // Append any remaining section groups that weren't in `sections` with safer fallbacks
    Object.keys(sectionGroups).forEach(sid => {
      if (!added.has(sid)) {
        const meta = detailedSections[sid] || sections.find(s => String(s.id) === sid) || (sid === 'no-section' ? { title: 'Général' } : { title: `Section ${sid}` });
        sectionList.push({ sid, group: sectionGroups[sid], meta });
        added.add(sid);
      }
    });

    console.log("sectionList: ", sectionList);

    return (
      <Box>
        {sectionList.map(({ sid, group, meta }) => {
          const sectionMeta = meta || { title: sid === 'no-section' ? 'Général' : `Section ${sid}` };
          return (
            <Card key={sid} elevation={2} sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
                  {sectionMeta.title || sectionMeta.name || `Section ${sid}`}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {group.responses.length > 0 ? (
                  <Grid container spacing={4}>
                    <List sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper' }}>
                    {group.responses.map((response, idx) => (
                      <>
                        <ListItem alignItems="flex-start" key={idx} sx={{ py: 2 }}>
                          {/* <ListItemAvatar>
                            {getResponseTypeIcon(response.content?.content_type)}
                          </ListItemAvatar> */}
                          <ListItemText
                            secondary={
                              <React.Fragment>
                                <Typography variant="h6" gutterBottom color="primary" sx={{ fontSize: '1rem' }}>
                                   {getResponseDisplayName(response)}
                                   {/* {response.id} */}
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 1, minHeight: '2rem' }}>
                                  {getResponseDisplayValue(response)} {getResponseDisplaySuffix(response)}
                                </Typography>
                              </React.Fragment>
                            }
                          />
                        </ListItem>
                        <Divider variant="inset" component="li" />
                      </>
                       
                    ))}
                    </List>
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Aucune réponse pour cette section.
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  };

  if (loading) {
    return (
      <div className="content-body">
        <div className="container-fluid">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Box textAlign="center">
              <CircularProgress />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Chargement des détails de la demande...
              </Typography>
            </Box>
          </Box>
        </div>
      </div>
    );
  }

  if (error || !demande) {
    return (
      <div className="content-body">
        <div className="container-fluid">
          <Alert severity="warning">
            <Typography variant="h6" gutterBottom>
              Demande non disponible
            </Typography>
            <Typography variant="body2">
              {error || 'Demande non trouvée'}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/be-projects')}
              >
                Retour aux projets
              </Button>
            </Box>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="content-body">
      <div className="container-fluid">
        {/* Header de la demande */}
        <Box sx={{ bgcolor: 'background.paper', mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 3 }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <Avatar sx={{ bgcolor: 'white', color: 'primary.main', width: 56, height: 56 }}>
                  <AssignmentIcon />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h4" fontWeight="bold">
                  Demande # {demande.reference_chantier || demande.id}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {demande.name} • {demande.project_step}
                  {project && ` • ${project.name}`}
                </Typography>
              </Grid>
              <Grid item>
                <Chip 
                  label={demande.status || demande.project_step} 
                  color={getStatusColor(demande.status || demande.project_step)}
                  sx={{ color: 'white', fontWeight: 'bold' }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Informations générales dans le header */}
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Informations du demandeur */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon />
                  Informations du demandeur
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Nom :</strong></TableCell>
                        <TableCell>{demande.name || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Email :</strong></TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <EmailIcon fontSize="small" />
                            {demande.email || 'N/A'}
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Téléphone :</strong></TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <PhoneIcon fontSize="small" />
                            {demande.numero_tel || 'N/A'}
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Adresse :</strong></TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <LocationIcon fontSize="small" />
                            {demande.adresse || 'N/A'}
                            {demande.post_code && ` (${demande.post_code})`}
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Infos complémentaires :</strong></TableCell>
                        <TableCell>{demande.complementary_info || 'N/A'}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              
              {/* Informations du projet */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HomeIcon />
                  Informations du projet
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Étape :</strong></TableCell>
                        <TableCell>
                          <Chip 
                            label={demande.project_step} 
                            color={getStatusColor(demande.project_step)} 
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Date de début :</strong></TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CalendarIcon fontSize="small" />
                            {formatDate(demande.project_start_date)}
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Référence chantier :</strong></TableCell>
                        <TableCell>{demande.reference_chantier || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Pro du Bâtiment :</strong></TableCell>
                        <TableCell>
                          {demande.has_technician ? (
                            <Chip label="Oui" color="success" size="small" />
                          ) : (
                            <Chip label="Non" color="warning" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Client Rullier :</strong></TableCell>
                        <TableCell>
                          {demande.need_technician ? (
                            <Chip label="Oui" color="info" size="small" />
                          ) : (
                            <Chip label="Non" color="default" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Onglets */}
        <Paper elevation={3} sx={{ borderRadius: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  minHeight: 64,
                  textTransform: 'none',
                  fontWeight: 'bold'
                }
              }}
            >
              <Tab
                label="Réponses"
                icon={<AssignmentIcon />}
                iconPosition="start"
              />
              <Tab
                label="Documents"
                icon={<AttachFileIcon />}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* Contenu des onglets */}
          <TabPanel value={activeTab} index={0}>
            {renderReponses()}
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            {renderDocuments()}
          </TabPanel>
        </Paper>

        {/* Actions */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/be-projects')}
          >
            Retour aux projets
          </Button>
        </Box>
      </div>
    </div>
  );
};

export default DemandeDetailsPage;